import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, map, of, tap } from 'rxjs';

import { TerminalV2Service } from '@app/api/terminal-v2/terminal-v2.service';
import { TERMINAL_V2_TEXT_INFO_TYPE } from '@app/models/api/terminal-v2/terminal-v2.model';
import { navigationActions } from '@app/store/actions/navigation.actions';
import { TerminalV2NotificationMessageService } from '../../../terminal-v2/services/terminal-v2-notification-message.service';
import { loginActions } from '../actions/login.actions';
import { persistanceActions } from '../actions/persistance.actions';
import { terminalV2Actions, terminalV2LoginActions } from '../actions/terminal-v2.actions';

@Injectable()
export class TerminalV2Effects {
  public readonly terminalV2Login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(terminalV2LoginActions.start),
      exhaustMap(({ params }) =>
        this.terminalService.activateTerminalV2(params.code).pipe(
          map(response => terminalV2LoginActions.success({ response: response })),
          catchError((response: HttpErrorResponse) =>
            of(
              terminalV2LoginActions.error({
                validationErrors: response.error.errors,
                errorMessage: response.error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly saveTerminalV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(terminalV2LoginActions.success),
      map(({ response }) => terminalV2Actions.save({ terminal: response.terminal })),
    ),
  );

  public readonly persistTerminalV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(terminalV2Actions.save),
      map(({ terminal }) => persistanceActions.persistTerminalV2({ terminal })),
    ),
  );

  public readonly removeTerminalV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(terminalV2Actions.remove),
      map(() => persistanceActions.removeTerminalV2()),
    ),
  );

  public readonly storeUserV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(terminalV2LoginActions.success),
      map(({ response }) => loginActions.success({ response, skipNavigation: true })),
    ),
  );

  public readonly redirectToTerminalV2UserInput$ = createEffect(() =>
    this.actions$.pipe(
      ofType(terminalV2LoginActions.success),
      tap(() => document.documentElement.requestFullscreen()),
      map(() => navigationActions.navigateTo({ commands: ['/', 'terminal-v2', 'user-login'] })),
    ),
  );

  public readonly successTerminalV2Notification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(terminalV2LoginActions.success),
        tap(res => {
          this.terminalNotificationMessageService.setNewNotification(
            this.translateService.instant('fe.terminal_v2.info.login_to_terminal_success--terminal_name', {
              terminal_name: res.response.terminal.name,
            }),
            TERMINAL_V2_TEXT_INFO_TYPE.SUCCESS,
          );
        }),
      ),
    { dispatch: false },
  );

  public readonly errorTerminalV2Notification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(terminalV2LoginActions.error),
        tap(() => {
          this.terminalNotificationMessageService.setNewNotification(
            this.translateService.instant(this.translateService.instant('fe.terminal_v2.info.login_to_terminal_failed')),
            TERMINAL_V2_TEXT_INFO_TYPE.ERROR,
          );
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly terminalService: TerminalV2Service,
    private readonly translateService: TranslateService,
    private readonly terminalNotificationMessageService: TerminalV2NotificationMessageService,
  ) {}
}
